import { MouseEvent, ReactNode } from 'react';
import { Link } from '@tanstack/react-router';

import { cx } from '@libs/classnames';
import { RemoveIcon } from '@ui/icons/RemoveIcon';

import styles from './FancyBadge.module.css';

export type Props = {
  // round — без боковых отступов, контент по центру (круг для «?» и иконок).
  variant?: 'default' | 'round';
  className?: string;
  title?: string;
  href?: string;
  // по умолчанию ссылка открывается в новой вкладке
  inNewTab?: boolean;
  onClick?: () => void;
  onRemove?: () => void;
  removeTitle?: string;
  disabled?: boolean;
  children: ReactNode;
};

export function FancyBadge({
  variant = 'default',
  className,
  title,
  href,
  inNewTab = true,
  onClick,
  onRemove,
  removeTitle,
  disabled,
  children,
}: Props) {
  const handleRemove = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onRemove?.();
  };

  const badgeClassName = cx(className, styles.badge, {
    [styles.is_round]: variant === 'round',
    [styles.is_clickable]: !disabled && (!!href || onClick),
    [styles.is_disabled]: disabled,
  });

  const content = (
    <>
      <span className={styles.text}>{children}</span>

      {onRemove && (
        <button
          type="button"
          title={removeTitle}
          aria-label={removeTitle ?? 'Удалить'}
          onClick={handleRemove}
          className={styles.remove_btn}
        >
          <RemoveIcon className={styles.remove_btn__icon} />
        </button>
      )}
    </>
  );

  if (href && !disabled) {
    return (
      <Link
        className={badgeClassName}
        title={title}
        target={inNewTab ? '_blank' : undefined}
        to={href}
        onClick={onClick}
      >
        {content}
      </Link>
    );
  }

  // Кликабельный бейдж — настоящая кнопка: фокус и Enter/Space из коробки.
  // С onRemove внутри уже есть кнопка удаления, а вложенные button невалидны —
  // такой бейдж остается span'ом.
  if (onClick && !onRemove) {
    return (
      <button
        type="button"
        className={badgeClassName}
        title={title}
        disabled={disabled}
        onClick={onClick}
      >
        {content}
      </button>
    );
  }

  return (
    <span className={badgeClassName} title={title} onClick={disabled ? undefined : onClick}>
      {content}
    </span>
  );
}
